import { supabase } from './supabase';
import { sanitizeInput } from './sanitize';

export type ErrorLevel = 'critical' | 'error' | 'warning';

interface ErrorLogData {
  message: string;
  stack?: string;
  level: ErrorLevel;
  pageUrl: string;
  additionalData?: Record<string, unknown>;
}

class ErrorLogger {
  private sessionId: string;
  private logQueue: Map<string, number> = new Map();
  private readonly RATE_LIMIT_WINDOW = 60000;
  private readonly MAX_ERRORS_PER_WINDOW = 10;
  private readonly DEDUP_WINDOW = 300000;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
  }

  private getOrCreateSessionId(): string {
    const key = 'error_logger_session_id';
    let sessionId = sessionStorage.getItem(key);

    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      sessionStorage.setItem(key, sessionId);
    }

    return sessionId;
  }

  private generateErrorHash(message: string, stack?: string): string {
    const content = `${message}${stack || ''}`;
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return `error_${Math.abs(hash).toString(36)}`;
  }

  private sanitizeErrorMessage(message: string): string {
    const sensitivePatterns = [
      /Bearer\s+[\w-]+\.[\w-]+\.[\w-]+/gi,
      /token[=:]\s*[\w-]+/gi,
      /api[_-]?key[=:]\s*[\w-]+/gi,
      /password[=:]\s*\S+/gi,
      /email[=:]\s*\S+@\S+/gi,
    ];

    let sanitized = message;
    sensitivePatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    });

    return sanitizeInput(sanitized);
  }

  private async checkRateLimit(): Promise<boolean> {
    const now = Date.now();
    const recentErrors = Array.from(this.logQueue.entries())
      .filter(([, timestamp]) => now - timestamp < this.RATE_LIMIT_WINDOW);

    this.logQueue.clear();
    recentErrors.forEach(([hash, timestamp]) => {
      this.logQueue.set(hash, timestamp);
    });

    return recentErrors.length < this.MAX_ERRORS_PER_WINDOW;
  }

  private async checkDuplicate(errorHash: string): Promise<boolean> {
    const fiveMinutesAgo = new Date(Date.now() - this.DEDUP_WINDOW).toISOString();

    const { data, error } = await supabase
      .from('error_logs')
      .select('id')
      .eq('error_hash', errorHash)
      .gte('created_at', fiveMinutesAgo)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error checking duplicates:', error);
      return false;
    }

    return !!data;
  }

  private getUserAgent(): string {
    return navigator.userAgent || 'Unknown';
  }

  private async getCurrentUserId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch {
      return null;
    }
  }

  async log(data: ErrorLogData): Promise<void> {
    try {
      if (!await this.checkRateLimit()) {
        console.warn('Error logging rate limit exceeded');
        return;
      }

      const sanitizedMessage = this.sanitizeErrorMessage(data.message);
      const sanitizedStack = data.stack ? this.sanitizeErrorMessage(data.stack) : null;
      const errorHash = this.generateErrorHash(sanitizedMessage, sanitizedStack || '');

      if (await this.checkDuplicate(errorHash)) {
        return;
      }

      this.logQueue.set(errorHash, Date.now());

      const userId = await this.getCurrentUserId();

      const errorLog = {
        error_message: sanitizedMessage,
        error_stack: sanitizedStack,
        error_level: data.level,
        page_url: sanitizeInput(data.pageUrl),
        user_agent: this.getUserAgent(),
        session_id: this.sessionId,
        user_id: userId,
        error_hash: errorHash,
      };

      const { error } = await supabase
        .from('error_logs')
        .insert(errorLog);

      if (error) {
        console.error('Failed to log error to database:', error);
      }
    } catch (err) {
      console.error('Error logger failed:', err);
    }
  }

  async logCritical(message: string, stack?: string, additionalData?: Record<string, unknown>): Promise<void> {
    await this.log({
      message,
      stack,
      level: 'critical',
      pageUrl: window.location.href,
      additionalData,
    });
  }

  async logError(message: string, stack?: string, additionalData?: Record<string, unknown>): Promise<void> {
    await this.log({
      message,
      stack,
      level: 'error',
      pageUrl: window.location.href,
      additionalData,
    });
  }

  async logWarning(message: string, stack?: string, additionalData?: Record<string, unknown>): Promise<void> {
    await this.log({
      message,
      stack,
      level: 'warning',
      pageUrl: window.location.href,
      additionalData,
    });
  }

  async logErrorObject(error: Error, level: ErrorLevel = 'error'): Promise<void> {
    await this.log({
      message: error.message,
      stack: error.stack,
      level,
      pageUrl: window.location.href,
    });
  }
}

export const errorLogger = new ErrorLogger();
